<!-- Визуальная миниатюра PDF и точка входа в его модальное превью. -->
<template>
  <BaseInfoCard
    title="PDF документ"
    class="edo-document-pdf-card"
    :class="cardClasses"
    role="button"
    :tabindex="available ? 0 : -1"
    :aria-disabled="!available"
    @click="openPreview"
    @keydown.enter.prevent="openPreview"
    @keydown.space.prevent="openPreview"
  >
    <template #icon>
      <AppIcon name="documentOutline" :size="20" />
    </template>

    <div class="edo-document-pdf-card__preview" aria-hidden="true">
      <div class="edo-document-pdf-card__sheet">
        <span class="edo-document-pdf-card__badge">PDF</span>
        <span class="edo-document-pdf-card__sheet-title" />
        <span class="edo-document-pdf-card__sheet-line edo-document-pdf-card__sheet-line--wide" />
        <span class="edo-document-pdf-card__sheet-line" />
        <span class="edo-document-pdf-card__sheet-line edo-document-pdf-card__sheet-line--short" />
        <span class="edo-document-pdf-card__sheet-table" />
        <span class="edo-document-pdf-card__sheet-line edo-document-pdf-card__sheet-line--wide" />
        <span class="edo-document-pdf-card__sheet-line" />
        <span class="edo-document-pdf-card__sheet-line edo-document-pdf-card__sheet-line--short" />
      </div>

      <span class="edo-document-pdf-card__action">
        <AppIcon :name="pending ? 'sync' : 'eye'" :size="16" />
        {{ actionLabel }}
      </span>
    </div>

    <div class="edo-document-pdf-card__meta">
      <strong>Печатная форма</strong>
      <span>{{ description }}</span>
    </div>
  </BaseInfoCard>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";

export default {
  name: "EdoDocumentPdfPreviewCard",

  components: {
    AppIcon,
  },

  props: {
    available: { type: Boolean, default: false },
    pending: { type: Boolean, default: false },
  },

  emits: ["open"],

  computed: {
    cardClasses() {
      return {
        "edo-document-pdf-card--interactive": this.available,
        "edo-document-pdf-card--pending": this.pending,
        "edo-document-pdf-card--unavailable": !this.available,
      };
    },

    actionLabel() {
      if (this.pending) return "PDF формируется";
      if (this.available) return "Открыть PDF";
      return "PDF недоступен";
    },

    description() {
      if (this.pending) return "PDF формируется в Saby и появится автоматически.";
      if (this.available) return "Откройте печатную форму документа для просмотра и скачивания.";
      return "PDF и XML для этого документа пока недоступны.";
    },
  },

  methods: {
    openPreview() {
      if (this.available) this.$emit("open");
    },
  },
};
</script>

<style scoped>
.edo-document-pdf-card {
  min-width: 0;
  margin: 0;
  outline: none;
  transition: box-shadow 0.2s ease;
}

.edo-document-pdf-card--interactive {
  cursor: pointer;
}

.edo-document-pdf-card--interactive:hover,
.edo-document-pdf-card--interactive:focus-visible {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.1);
}

.edo-document-pdf-card__preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 190px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background:
    radial-gradient(circle at 18% 20%, rgba(148, 163, 184, 0.12), transparent 34%),
    #f3f5f7;
}

.edo-document-pdf-card__sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 126px;
  height: 160px;
  padding: 20px 14px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 3px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
  box-sizing: border-box;
  transform: rotate(-1.5deg);
  transition: transform 0.2s ease;
}

.edo-document-pdf-card__sheet::after {
  position: absolute;
  top: -1px;
  right: -1px;
  width: 18px;
  height: 18px;
  border-left: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(225deg, #f3f5f7 49%, #fff 50%);
  content: "";
}

.edo-document-pdf-card--interactive:hover .edo-document-pdf-card__sheet,
.edo-document-pdf-card--interactive:focus-visible .edo-document-pdf-card__sheet {
  transform: translateY(-3px) rotate(-1.5deg);
}

.edo-document-pdf-card__badge {
  position: absolute;
  top: 12px;
  left: 14px;
  color: #dc2626;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.edo-document-pdf-card__sheet-title,
.edo-document-pdf-card__sheet-line,
.edo-document-pdf-card__sheet-table {
  display: block;
  flex: 0 0 auto;
  border-radius: 2px;
  background: #d8dee7;
}

.edo-document-pdf-card__sheet-title {
  width: 56%;
  height: 7px;
  margin: 12px auto 2px;
  background: #94a3b8;
}

.edo-document-pdf-card__sheet-line {
  width: 78%;
  height: 4px;
}

.edo-document-pdf-card__sheet-line--wide {
  width: 100%;
}

.edo-document-pdf-card__sheet-line--short {
  width: 54%;
}

.edo-document-pdf-card__sheet-table {
  width: 100%;
  height: 34px;
  border: 1px solid #cbd5e1;
  background:
    linear-gradient(90deg, transparent 48%, #cbd5e1 49%, #cbd5e1 51%, transparent 52%),
    linear-gradient(transparent 48%, #cbd5e1 49%, #cbd5e1 51%, transparent 52%);
}

.edo-document-pdf-card__action {
  position: absolute;
  left: 50%;
  bottom: 14px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.88);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.2);
  transform: translateX(-50%);
}

.edo-document-pdf-card--pending .edo-document-pdf-card__action {
  background: rgba(71, 85, 105, 0.9);
}

.edo-document-pdf-card--unavailable .edo-document-pdf-card__preview {
  filter: grayscale(0.35);
  opacity: 0.68;
}

.edo-document-pdf-card__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
  min-width: 0;
}

.edo-document-pdf-card__meta strong {
  color: var(--color-dark);
  font-size: 14px;
  font-weight: 600;
}

.edo-document-pdf-card__meta span {
  color: #64748b;
  font-size: 14px;
  line-height: 1.45;
}

@media (prefers-reduced-motion: reduce) {
  .edo-document-pdf-card,
  .edo-document-pdf-card__sheet {
    transition: none;
  }
}
</style>
